import React, { useEffect, useState, useCallback } from 'react';
import connectUser from '../connectUser';
import Icon from '../ui/Icon';
import HiveButton from '../ui/HiveButton';
import { APP_REGISTRY } from '../registry/appRegistry';
import accessApi from '../accessApi';
import './appControl.css';

function randomPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 10; i += 1) out += chars.charAt(Math.floor(Math.random() * chars.length));
  return out;
}

// Apps a master-admin can grant/revoke = the live marketplace apps (standalone
// + personal). Admin/system routes (settings, folders) aren't granted here.
const GRANTABLE = APP_REGISTRY.filter((a) => a.status === 'live');
const USERS_PER_PAGE = 20;

function fullName(u) {
  return [u.firstname, u.lastname].filter(Boolean).join(' ') || u.username || u.userId;
}

function AppControl({ userInfo }) {
  const routes = userInfo?.routes ? [...userInfo.routes] : [];
  const isMaster = routes.includes('app-control');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState({}); // `${userId}:${appId}` -> true
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    // Pending access requests are reviewed in Updates now; App Control only
    // manages the user ↔ app grant matrix.
    accessApi
      .allUsers()
      .then((uRes) => {
        setUsers(Array.isArray(uRes?.data) ? uRes.data : []);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isMaster) load();
    else setLoading(false);
  }, [isMaster, load]);

  // Optimistically flip a user's route set after grant/revoke.
  const applyLocal = (userId, appId, granted) => {
    setUsers((prev) =>
      prev.map((u) => {
        if (u.userId !== userId) return u;
        const set = new Set(u.routes || []);
        if (granted) set.add(appId);
        else set.delete(appId);
        return { ...u, routes: [...set] };
      })
    );
  };

  const toggle = (user, app) => {
    const key = `${user.userId}:${app.id}`;
    if (busy[key]) return;
    const granted = (user.routes || []).includes(app.id);
    setBusy((b) => ({ ...b, [key]: true }));
    const call = granted ? accessApi.revoke(user.userId, app.id) : accessApi.grant(user.userId, app.id);
    call
      .then(() => applyLocal(user.userId, app.id, !granted))
      .catch(() => {})
      .finally(() => setBusy((b) => ({ ...b, [key]: false })));
  };

  const doDeleteUser = (u) => {
    accessApi
      .deleteUser(u.userId)
      .then((res) => {
        if (res?.data?.success === false) return;
        setDeletingUser(null);
        setUsers((prev) => prev.filter((x) => x.userId !== u.userId));
      })
      .catch(() => {});
  };

  const applyUserEdit = (userId, fields) => {
    setUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, ...fields } : u)));
  };

  // Search + paginate (client-side over the loaded list).
  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) =>
        `${u.firstname || ''} ${u.lastname || ''} ${u.username || ''}`.toLowerCase().includes(q)
      )
    : users;
  const pageCount = Math.max(1, Math.ceil(filtered.length / USERS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pageUsers = filtered.slice(safePage * USERS_PER_PAGE, safePage * USERS_PER_PAGE + USERS_PER_PAGE);

  if (!isMaster) {
    return (
      <div className="hive-container hive-screen">
        <div className="hive-empty hive-empty-lg">
          <div className="hive-empty-ic"><Icon name="ShieldAlert" size={30} /></div>
          <p className="hive-empty-title">Master-admin only</p>
          <p>App Control is available to platform master-admins.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hive-container hive-screen">
      <div className="hive-ac-head">
        <div className="hive-ac-headtop">
          <Icon name="ShieldCheck" size={22} className="hive-gold-text" />
          <h1 className="hive-h2">App Control</h1>
        </div>
        <p className="hive-ac-lede">
          Turn apps on or off for any user. Grants take effect the next time that user opens the app.
          Access requests are reviewed in <strong>Updates</strong>. (Billing / self-serve comes later.)
        </p>
      </div>

      {loading ? (
        <div className="hive-empty"><p>Loading…</p></div>
      ) : error ? (
        <div className="hive-empty"><p>Couldn’t load App Control. <button className="hive-link" onClick={load}>Retry</button></p></div>
      ) : (
        <>
          {/* Grant matrix — one card per user */}
          <section className="hive-screen-section">
            <div className="hive-ac-users-head">
              <h2 className="hive-section-title">Users &amp; apps</h2>
              <button type="button" className="hive-ac-adduser hive-tap" onClick={() => setShowAdd(true)}>
                <Icon name="UserPlus" size={16} /> Add user
              </button>
            </div>
            <div className="hive-ac-search">
              <Icon name="Search" size={16} className="hive-ac-search-ic" />
              <input
                className="hive-ac-search-input"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                placeholder="Search users by name or email…"
              />
              <span className="hive-ac-search-count">{filtered.length}</span>
            </div>

            {filtered.length === 0 ? (
              <p className="hive-subtle">No users match your search.</p>
            ) : (
              <>
                <div className="hive-card-list">
                  {pageUsers.map((u) => (
                    <div key={u.userId} className="hive-ac-user">
                      <div className="hive-ac-user-head">
                        <div className="hive-ac-user-avatar">{fullName(u).charAt(0).toUpperCase()}</div>
                        <div className="hive-ac-user-id">
                          <div className="hive-card-title">{fullName(u)}</div>
                          <div className="hive-meta">{u.username}</div>
                        </div>
                        <div className="hive-ac-user-actions">
                          <button
                            type="button"
                            className="hive-ac-iconbtn hive-tap"
                            onClick={() => setEditingUser(u)}
                            aria-label="Edit user"
                          >
                            <Icon name="Pencil" size={15} />
                          </button>
                          <button
                            type="button"
                            className="hive-ac-iconbtn hive-ac-iconbtn-danger hive-tap"
                            onClick={() => setDeletingUser(u)}
                            aria-label="Delete user"
                          >
                            <Icon name="Trash2" size={15} />
                          </button>
                        </div>
                      </div>
                      <div className="hive-ac-chips">
                        {GRANTABLE.map((app) => {
                          const granted = (u.routes || []).includes(app.id);
                          const key = `${u.userId}:${app.id}`;
                          return (
                            <button
                              key={app.id}
                              type="button"
                              className={`hive-ac-chip hive-tap${granted ? ' is-on' : ''}`}
                              disabled={busy[key]}
                              onClick={() => toggle(u, app)}
                            >
                              <Icon name={granted ? 'Check' : 'Plus'} size={13} strokeWidth={2.6} />
                              {app.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {pageCount > 1 && (
                  <div className="hive-ac-pager">
                    <button
                      type="button"
                      className="hive-ac-pagebtn hive-tap"
                      disabled={safePage === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      <Icon name="ChevronLeft" size={16} /> Prev
                    </button>
                    <span className="hive-ac-pageinfo">Page {safePage + 1} of {pageCount}</span>
                    <button
                      type="button"
                      className="hive-ac-pagebtn hive-tap"
                      disabled={safePage >= pageCount - 1}
                      onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    >
                      Next <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={(fields) => {
            applyUserEdit(editingUser.userId, fields);
            setEditingUser(null);
          }}
        />
      )}
      {deletingUser && (
        <DeleteUserConfirm
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onConfirm={() => doDeleteUser(deletingUser)}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    firstname: user.firstname || '',
    lastname: user.lastname || '',
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const submit = () => {
    setErr(null);
    setSaving(true);
    const payload = { userId: user.userId, firstname: form.firstname, lastname: form.lastname };
    if (form.password) payload.password = form.password;
    accessApi
      .updateUser(payload)
      .then((res) => {
        if (res?.data?.success === false) {
          setErr(res.data.message || 'Could not update the user.');
          setSaving(false);
          return;
        }
        onSaved({ firstname: form.firstname, lastname: form.lastname });
      })
      .catch(() => {
        setErr('Could not update the user.');
        setSaving(false);
      });
  };

  return (
    <div className="hive-modal-backdrop" onMouseDown={onClose}>
      <div className="hive-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hive-modal-head">
          <h3 className="hive-modal-title">Edit user</h3>
          <button type="button" className="hive-modal-close hive-tap" onClick={onClose} aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>
        <p className="hive-modal-sub">{user.username}</p>

        <div className="hive-modal-row2">
          <label className="hive-field">
            <span className="hive-field-label">First name</span>
            <input className="hive-field-input" value={form.firstname} onChange={set('firstname')} />
          </label>
          <label className="hive-field">
            <span className="hive-field-label">Last name</span>
            <input className="hive-field-input" value={form.lastname} onChange={set('lastname')} />
          </label>
        </div>

        <label className="hive-field">
          <span className="hive-field-label">
            New password <span style={{ opacity: 0.6, fontWeight: 400 }}>(leave blank to keep)</span>
          </span>
          <div className="hive-field-pw">
            <input className="hive-field-input" value={form.password} onChange={set('password')} placeholder="••••••••" />
            <button
              type="button"
              className="hive-field-gen hive-tap"
              onClick={() => setForm((f) => ({ ...f, password: randomPassword() }))}
            >
              <Icon name="RefreshCw" size={15} /> New
            </button>
          </div>
        </label>

        {err && <div className="hive-modal-err">{err}</div>}

        <div className="hive-modal-actions">
          <HiveButton variant="ghost" onClick={onClose}>Cancel</HiveButton>
          <HiveButton variant="gold" disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save changes'}</HiveButton>
        </div>
      </div>
    </div>
  );
}

function DeleteUserConfirm({ user, onClose, onConfirm }) {
  const [saving, setSaving] = useState(false);
  return (
    <div className="hive-modal-backdrop" onMouseDown={onClose}>
      <div className="hive-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hive-modal-head">
          <h3 className="hive-modal-title">Delete user?</h3>
          <button type="button" className="hive-modal-close hive-tap" onClick={onClose} aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>
        <p className="hive-modal-sub">
          Permanently delete {fullName(user)} ({user.username}) and their access. This can’t be undone.
        </p>
        <div className="hive-modal-actions">
          <HiveButton variant="ghost" onClick={onClose}>Cancel</HiveButton>
          <button
            type="button"
            className="hive-danger-btn hive-tap"
            disabled={saving}
            onClick={() => {
              setSaving(true);
              onConfirm();
            }}
          >
            {saving ? 'Deleting…' : 'Delete user'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    username: '',
    password: randomPassword(),
  });
  const [apps, setApps] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Capture the value synchronously — React 16 pools/recycles the synthetic
  // event, so reading e.target inside the (async) functional updater throws.
  const set = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };
  const toggleApp = (id) =>
    setApps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    if (!form.username.trim()) return setErr('Email is required.');
    if (!form.password) return setErr('Password is required.');
    setErr(null);
    setSaving(true);
    accessApi
      .createUser({ ...form, username: form.username.trim(), apps: [...apps] })
      .then((res) => {
        if (res?.data?.success === false) {
          setErr(res.data.message || 'Could not create the account.');
          setSaving(false);
          return;
        }
        onCreated();
      })
      .catch(() => {
        setErr('Could not create the account.');
        setSaving(false);
      });
  };

  return (
    <div className="hive-modal-backdrop" onMouseDown={onClose}>
      <div className="hive-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hive-modal-head">
          <h3 className="hive-modal-title">Add user</h3>
          <button type="button" className="hive-modal-close hive-tap" onClick={onClose} aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>
        <p className="hive-modal-sub">Create an individual account and share the login. No company needed.</p>

        <div className="hive-modal-row2">
          <label className="hive-field">
            <span className="hive-field-label">First name</span>
            <input className="hive-field-input" value={form.firstname} onChange={set('firstname')} />
          </label>
          <label className="hive-field">
            <span className="hive-field-label">Last name</span>
            <input className="hive-field-input" value={form.lastname} onChange={set('lastname')} />
          </label>
        </div>

        <label className="hive-field">
          <span className="hive-field-label">Email</span>
          <input className="hive-field-input" type="email" value={form.username} onChange={set('username')} placeholder="user@example.com" />
        </label>

        <label className="hive-field">
          <span className="hive-field-label">Temporary password</span>
          <div className="hive-field-pw">
            <input className="hive-field-input" value={form.password} onChange={set('password')} />
            <button type="button" className="hive-field-gen hive-tap" onClick={() => setForm((f) => ({ ...f, password: randomPassword() }))}>
              <Icon name="RefreshCw" size={15} /> New
            </button>
          </div>
        </label>

        <div className="hive-field">
          <span className="hive-field-label">Apps to grant</span>
          <div className="hive-ac-chips">
            {APP_REGISTRY.filter((a) => a.status === 'live').map((app) => {
              const on = apps.has(app.id);
              return (
                <button key={app.id} type="button" className={`hive-ac-chip hive-tap${on ? ' is-on' : ''}`} onClick={() => toggleApp(app.id)}>
                  <Icon name={on ? 'Check' : 'Plus'} size={13} strokeWidth={2.6} />
                  {app.name}
                </button>
              );
            })}
          </div>
        </div>

        {err && <div className="hive-modal-err">{err}</div>}

        <div className="hive-modal-actions">
          <HiveButton variant="ghost" onClick={onClose}>Cancel</HiveButton>
          <HiveButton variant="gold" disabled={saving} onClick={submit}>
            {saving ? 'Creating…' : 'Create account'}
          </HiveButton>
        </div>
      </div>
    </div>
  );
}

export default connectUser(AppControl);
